<script lang="tsx" setup>
import { KirbyImageData } from "@/queries/image"
import { ref, onMounted, onBeforeUnmount } from "vue"
import SVG_Close from "../SVGs/SVG_Close.vue"
import { computeZoomRect, useZoom } from "@/composables/use-zoom"
import { useImageZoom, PAN_STEP } from "@/composables/use-image-zoom"

const props = defineProps<{
  data?: KirbyImageData | null
}>()

const { zoomSourceElement } = useZoom()
const {
  init,
  dispose,
  zoomIn,
  zoomOut,
  reset,
  resetAnimated,
  getTransform,
  panBy,
} = useImageZoom()

const emit = defineEmits<{
  (e: "close"): void
}>()

const dialog = ref<HTMLDivElement>()
const closeButton = ref<HTMLButtonElement>()
const container = ref<HTMLDivElement>()
const image = ref<HTMLImageElement>()
const thumbnailContainer = ref<HTMLDivElement>()

// Captured at mount so focus can return to the element that opened the dialog.
// Read here (not on unmount) because the flight teardown nulls zoomSourceElement.
let triggerEl: HTMLElement | null = null
const fullImageLoaded = ref(props.data?.mime === "image/gif")

const rect = ref(
  props.data
    ? computeZoomRect(props.data.width / props.data.height)
    : { width: 0, height: 0 },
)

const onResize = () => {
  if (props.data)
    rect.value = computeZoomRect(props.data.width / props.data.height)
  const clone = thumbnailContainer.value
    ?.firstElementChild as HTMLElement | null
  if (clone) {
    clone.style.width = `${rect.value.width}px`
    clone.style.height = `${rect.value.height}px`
  }
}

const isClosing = ref(false)

const handleClose = async () => {
  if (isClosing.value) return
  isClosing.value = true
  await resetAnimated(300)
  emit("close")
}

// Keep Tab focus inside the dialog by cycling between its first/last buttons.
const trapFocus = (e: KeyboardEvent) => {
  const focusable = dialog.value?.querySelectorAll<HTMLElement>("button")
  if (!focusable || focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement
  if (e.shiftKey && active === first) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

const onKeyDown = (e: KeyboardEvent) => {
  switch (e.key) {
    case "Escape":
      handleClose()
      return
    case "Tab":
      trapFocus(e)
      return
    case "+":
    case "=":
      e.preventDefault()
      zoomIn()
      return
    case "-":
    case "_":
      e.preventDefault()
      zoomOut()
      return
    case "0":
      e.preventDefault()
      reset()
      return
    case "ArrowUp":
      e.preventDefault()
      panBy(0, PAN_STEP)
      return
    case "ArrowDown":
      e.preventDefault()
      panBy(0, -PAN_STEP)
      return
    case "ArrowLeft":
      e.preventDefault()
      panBy(PAN_STEP, 0)
      return
    case "ArrowRight":
      e.preventDefault()
      panBy(-PAN_STEP, 0)
      return
  }
}

onMounted(() => {
  window.addEventListener("keydown", onKeyDown)

  window.addEventListener("resize", onResize)

  // Remember the trigger so focus returns there on close, then move focus into
  // the dialog so its controls and Escape are immediately keyboard-discoverable.
  triggerEl = zoomSourceElement.value?.closest("button") ?? null
  closeButton.value?.focus()

  if (zoomSourceElement.value && thumbnailContainer.value) {
    const clone = zoomSourceElement.value.cloneNode(true) as HTMLImageElement
    clone.removeAttribute("style")
    // Drop the source tag so hideZoomSource() can't match (and hide) the clone.
    clone.removeAttribute("data-zoom-id")
    clone.className = "object-contain select-none"
    clone.style.width = `${rect.value.width}px`
    clone.style.height = `${rect.value.height}px`
    clone.draggable = false
    thumbnailContainer.value.appendChild(clone)
  }

  if (props.data && props.data.mime !== "image/gif") {
    const hi = new Image()
    hi.src = props.data.url
    hi.onload = () => {
      fullImageLoaded.value = true
    }
  }

  if (container.value) init(container.value)
})

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown)
  window.removeEventListener("resize", onResize)
  dispose()
  // Return focus to the trigger if it's still in the DOM (preventScroll avoids
  // a jump mid flight-out animation).
  if (triggerEl?.isConnected) triggerEl.focus({ preventScroll: true })
})
</script>

<template>
  <div
    ref="dialog"
    role="dialog"
    aria-modal="true"
    :aria-label="data?.alt ? `Zoomed image: ${data.alt}` : 'Zoomed image'"
    class="fixed top-0 left-0 h-full inset-0 z-60"
  >
    <div
      ref="container"
      class="absolute w-full h-full flex items-center justify-center touch-none"
    >
      <div
        v-if="!fullImageLoaded"
        ref="thumbnailContainer"
        class="select-none"
      />
      <img
        v-else-if="data"
        ref="image"
        draggable="false"
        class="select-none"
        :style="{ width: `${rect.width}px`, height: `${rect.height}px` }"
        :src="data.url"
        :alt="data.alt"
      />
    </div>

    <!-- Close button -->
    <button
      ref="closeButton"
      v-motion-fade
      class="absolute top-3 right-3 z-50 p-4 border cursor-pointer bg-white md:hover:bg-black md:hover:text-white touch-auto"
      @click="handleClose"
      @touchend.stop.prevent="handleClose"
      aria-label="Close zoom"
    >
      <div class="w-4 h-4">
        <SVG_Close />
      </div>
    </button>

    <!-- Zoom controls -->
    <div
      class="absolute bottom-3 right-3 z-50 flex flex-col gap-2 touch-auto"
      v-motion-fade
    >
      <button
        v-for="f in [zoomIn, zoomOut]"
        class="w-12 h-12 flex items-center justify-center border border-current cursor-pointer bg-white md:hover:bg-black md:hover:text-white"
        @click="f"
        @touchend.stop.prevent="f()"
        :aria-label="f === zoomIn ? 'Zoom in' : 'Zoom out'"
      >
        <div class="w-4 h-4">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              vector-effect="non-scaling-stroke"
              x1="8"
              y1="0.5"
              x2="8"
              y2="15.5"
              stroke="currentColor"
              stroke-width="1"
              v-if="f === zoomIn"
            />
            <line
              vector-effect="non-scaling-stroke"
              x1="0.5"
              y1="8"
              x2="15.5"
              y2="8"
              stroke="currentColor"
              stroke-width="1"
            />
          </svg>
        </div>
      </button>
    </div>
  </div>
</template>
